import { isDutchieEmbedProductUrl } from './attribution'

/**
 * AIQ ecom menus enable kiosk mode via `?kiosk=true` on the host page URL.
 * When true, recommendation product URLs must be rewritten to kiosk PDPs.
 */
export function isDispenseKioskMode(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).get('kiosk') === 'true'
  } catch {
    return false
  }
}

function toAbsoluteUrl(productUrl: string): string {
  return /^https?:\/\//.test(productUrl) ? productUrl : `https://${productUrl}`
}

/** Last two path segments ({brand}/{slug}), matching widgetapi extractProductSlug. */
export function extractProductSlug(productURL: string): string {
  try {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://localhost'
    const u = new URL(productURL, origin)
    const parts = u.pathname.split('/').filter(Boolean)
    if (parts.length < 2) return ''
    return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`
  } catch {
    return ''
  }
}

export type KioskLocation = {
  origin: string
  pathname: string
}

/**
 * Rebuild a product PDP URL on the current kiosk host so recs do not leave
 * menus-dev / custom domains for menus.dispenseapp.com or the main menu.
 */
export function toKioskProductUrl(
  productUrl: string,
  location: KioskLocation = typeof window !== 'undefined'
    ? { origin: window.location.origin, pathname: window.location.pathname }
    : { origin: '', pathname: '' }
): string {
  const absolute = toAbsoluteUrl(productUrl)
  const slug = extractProductSlug(absolute)

  if (location.origin) {
    const venueMenuMatch = location.pathname.match(/^\/([^/]+)\/menu(?:\/|$)/)
    if (venueMenuMatch && slug) {
      return `${location.origin}/${venueMenuMatch[1]}/menu/${slug}?kiosk=true`
    }
    if (slug) {
      return `${location.origin}/${slug}?kiosk=true`
    }
  }

  try {
    const u = new URL(absolute)
    u.searchParams.set('kiosk', 'true')
    return u.toString()
  } catch {
    return productUrl
  }
}

/** Absolute product URL, rewritten onto the current kiosk host when applicable. */
export function resolveProductNavigationUrl(productUrl: string): string {
  const absolute = toAbsoluteUrl(productUrl)
  if (isDutchieEmbedProductUrl(absolute)) return absolute
  if (!isDispenseKioskMode()) return absolute
  return toKioskProductUrl(absolute)
}

/**
 * Same-origin menus navigation without a full reload when the host exposes
 * __dispenseNavigate. Falls back to assigning location.href.
 */
export function navigateToProductUrl(url: string): void {
  if (typeof window === 'undefined') return
  if (typeof window.__dispenseNavigate === 'function') {
    window.__dispenseNavigate(url)
    return
  }
  window.location.href = url
}

declare global {
  interface Window {
    __dispenseNavigate?: (url: string) => void
  }
}
